"use client";
import {
  Button,
  Field,
  Input,
  makeStyles,
  Textarea,
  tokens,
} from "@fluentui/react-components";
import { DatePicker, DayOfWeek } from "@fluentui/react";
import {
  Dismiss20Regular,
  Save20Regular,
  Send20Regular,
} from "@fluentui/react-icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useSystemInfoBannerGet,
  useUpdateSystemInfoBanner,
} from "../hooks/useSystemInfoBanner";
import {
  SystemInfoBanner,
  UpsertSystemInfoBannerRequest,
} from "../models/SystemInfoBanner";
import { systemInfoPickerStrings } from "../services/SystemInfoService";

type ScheduleForm = {
  validFromDate: string;
  validFromTime: string;
  validToDate: string;
  validToTime: string;
};

const useClasses = makeStyles({
  root: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    maxWidth: "900px",
  },
  scheduleFields: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: tokens.spacingHorizontalL,
  },
  scheduleGroup: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  dateTimeFields: {
    display: "grid",
    gridTemplateColumns: "1fr 120px",
    gap: tokens.spacingHorizontalS,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
  },
});

function SystemInfoBannerAdminPage() {
  const { data: systemInfoBanner } = useSystemInfoBannerGet();

  return (
    <SystemInfoBannerAdminForm
      key={getSystemInfoBannerStateKey(systemInfoBanner)}
      systemInfoBanner={systemInfoBanner}
    />
  );
}

type SystemInfoBannerAdminFormProps = {
  systemInfoBanner?: SystemInfoBanner;
};

function SystemInfoBannerAdminForm({
  systemInfoBanner,
}: SystemInfoBannerAdminFormProps) {
  const classes = useClasses();
  const { t } = useTranslation();
  const { mutateAsync: saveSystemInfoBanner, isPending: isSaving } =
    useUpdateSystemInfoBanner();
  const initialBannerForm = createBannerForm(systemInfoBanner);
  const [bannerForm, setBannerForm] =
    useState<UpsertSystemInfoBannerRequest>(initialBannerForm);
  const [savedBannerForm, setSavedBannerForm] =
    useState<UpsertSystemInfoBannerRequest>(initialBannerForm);
  const [scheduleForm, setScheduleForm] = useState<ScheduleForm>(
    createScheduleForm(
      initialBannerForm.validFromUtc,
      initialBannerForm.validToUtc
    )
  );
  const hasInvalidValidFromTime = isScheduleTimeInvalid(
    scheduleForm.validFromDate,
    scheduleForm.validFromTime
  );
  const hasInvalidValidToTime = isScheduleTimeInvalid(
    scheduleForm.validToDate,
    scheduleForm.validToTime
  );
  const hasInvalidScheduleTime =
    hasInvalidValidFromTime || hasInvalidValidToTime;
  const hasInvalidScheduleRange = isScheduleRangeInvalid(scheduleForm);
  const hasInvalidSchedule = hasInvalidScheduleTime || hasInvalidScheduleRange;
  const [currentTime, setCurrentTime] = useState(() => new Date());

  useEffect(() => {
    const intervalId = window.setInterval(
      () => setCurrentTime(new Date()),
      30_000
    );

    return () => window.clearInterval(intervalId);
  }, []);

  const isBannerPublishedAndNotExpired = isPublishedAndNotExpired(
    bannerForm,
    scheduleForm,
    currentTime
  );
  const hasExpiredSchedule = isScheduleExpired(scheduleForm, currentTime);
  const cannotPublishExpiredSchedule =
    !isBannerPublishedAndNotExpired && hasExpiredSchedule;
  let validToValidationMessage: string | undefined;
  let validToValidationState: "error" | "warning" | "none" = "none";

  if (hasInvalidValidToTime) {
    validToValidationMessage = t("systemInfoBanner.timeValidationMessage");
    validToValidationState = "error";
  } else if (hasInvalidScheduleRange) {
    validToValidationMessage = t("systemInfoBanner.dateRangeValidationMessage");
    validToValidationState = "error";
  } else if (hasExpiredSchedule) {
    validToValidationMessage = t(
      "systemInfoBanner.expiredScheduleValidationMessage"
    );
    validToValidationState = "warning";
  }

  const updateMessage = (message: string) => {
    setBannerForm({ ...bannerForm, message });
  };

  const updateValidFromDate = (value: string) => {
    const validFromTime = value
      ? getTimeOrDefault(scheduleForm.validFromTime)
      : "";

    setScheduleForm({
      ...scheduleForm,
      validFromDate: value,
      validFromTime,
    });
    setBannerForm({
      ...bannerForm,
      validFromUtc: toUtcIsoString(value, validFromTime),
    });
  };

  const updateValidFromTime = (value: string) => {
    const validFromTime = formatTimeInputValue(value);

    setScheduleForm({
      ...scheduleForm,
      validFromTime,
    });
    setBannerForm({
      ...bannerForm,
      validFromUtc: isCompleteTimeInput(validFromTime)
        ? toUtcIsoString(scheduleForm.validFromDate, validFromTime)
        : bannerForm.validFromUtc,
    });
  };

  const updateValidToDate = (value: string) => {
    const validToTime = value ? getTimeOrDefault(scheduleForm.validToTime) : "";

    setScheduleForm({
      ...scheduleForm,
      validToDate: value,
      validToTime,
    });
    setBannerForm({
      ...bannerForm,
      validToUtc: toUtcIsoString(value, validToTime),
    });
  };

  const updateValidToTime = (value: string) => {
    const validToTime = formatTimeInputValue(value);

    setScheduleForm({
      ...scheduleForm,
      validToTime,
    });
    setBannerForm({
      ...bannerForm,
      validToUtc: isCompleteTimeInput(validToTime)
        ? toUtcIsoString(scheduleForm.validToDate, validToTime)
        : bannerForm.validToUtc,
    });
  };

  const saveBanner = async (request: UpsertSystemInfoBannerRequest) => {
    const savedBanner = await saveSystemInfoBanner(request);
    const savedBannerForm = createBannerForm(savedBanner);

    setBannerForm(savedBannerForm);
    setSavedBannerForm(savedBannerForm);
    setScheduleForm(
      createScheduleForm(savedBanner.validFromUtc, savedBanner.validToUtc)
    );
  };

  const handleSave = async () => {
    if (hasInvalidSchedule) {
      return;
    }

    await saveBanner(
      createBannerRequest(bannerForm, scheduleForm, bannerForm.isPublished)
    );
  };

  const handleTogglePublish = async () => {
    if (hasInvalidSchedule || cannotPublishExpiredSchedule) {
      return;
    }

    await saveBanner(
      createBannerRequest(
        bannerForm,
        scheduleForm,
        !isBannerPublishedAndNotExpired
      )
    );
  };

  const hasUnsavedEditableChanges = hasEditableChanges(
    savedBannerForm,
    createBannerRequest(bannerForm, scheduleForm, bannerForm.isPublished)
  );

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <h2>{t("systemInfoBanner.title")}</h2>
      </div>
      <div className={classes.form}>
        <Field label={t("systemInfoBanner.messageLabel")} required>
          <Textarea
            value={bannerForm.message}
            resize="vertical"
            rows={5}
            onChange={(_ev, data) => updateMessage(data.value)}
          />
        </Field>
        <div className={classes.scheduleFields}>
          <div className={classes.scheduleGroup}>
            <Field
              label={t("systemInfoBanner.validFromLabel")}
              validationMessage={
                hasInvalidValidFromTime
                  ? t("systemInfoBanner.timeValidationMessage")
                  : undefined
              }
              validationState={hasInvalidValidFromTime ? "error" : "none"}
            >
              <div className={classes.dateTimeFields}>
                <DatePicker
                  firstDayOfWeek={DayOfWeek.Monday}
                  formatDate={formatSwedishDate}
                  strings={systemInfoPickerStrings}
                  placeholder={t("systemInfoBanner.datePlaceholder")}
                  ariaLabel={t("systemInfoBanner.dateLabel")}
                  maxDate={getDatePickerValue(scheduleForm.validToDate)}
                  value={getDatePickerValue(scheduleForm.validFromDate)}
                  onSelectDate={(date) =>
                    updateValidFromDate(toDateInputValue(date))
                  }
                />
                <Input
                  type="text"
                  lang="sv-SE"
                  inputMode="numeric"
                  maxLength={5}
                  pattern="[0-9:]*"
                  placeholder={t("systemInfoBanner.timePlaceholder")}
                  aria-label={t("systemInfoBanner.timeLabel")}
                  disabled={!scheduleForm.validFromDate}
                  value={scheduleForm.validFromTime}
                  onChange={(_event, data) => updateValidFromTime(data.value)}
                />
              </div>
            </Field>
          </div>
          <div className={classes.scheduleGroup}>
            <Field
              label={t("systemInfoBanner.validToLabel")}
              validationMessage={validToValidationMessage}
              validationState={validToValidationState}
            >
              <div className={classes.dateTimeFields}>
                <DatePicker
                  firstDayOfWeek={DayOfWeek.Monday}
                  formatDate={formatSwedishDate}
                  strings={systemInfoPickerStrings}
                  placeholder={t("systemInfoBanner.datePlaceholder")}
                  ariaLabel={t("systemInfoBanner.dateLabel")}
                  minDate={getDatePickerValue(scheduleForm.validFromDate)}
                  value={getDatePickerValue(scheduleForm.validToDate)}
                  onSelectDate={(date) =>
                    updateValidToDate(toDateInputValue(date))
                  }
                />
                <Input
                  type="text"
                  lang="sv-SE"
                  inputMode="numeric"
                  maxLength={5}
                  pattern="[0-9:]*"
                  placeholder={t("systemInfoBanner.timePlaceholder")}
                  aria-label={t("systemInfoBanner.timeLabel")}
                  disabled={!scheduleForm.validToDate}
                  value={scheduleForm.validToTime}
                  onChange={(_event, data) => updateValidToTime(data.value)}
                />
              </div>
            </Field>
          </div>
        </div>
        <div className={classes.actions}>
          <Button
            appearance="secondary"
            icon={<Save20Regular />}
            disabled={
              isSaving || hasInvalidSchedule || !hasUnsavedEditableChanges
            }
            onClick={handleSave}
          >
            {t("systemInfoBanner.save")}
          </Button>
          <Button
            appearance="primary"
            icon={
              isBannerPublishedAndNotExpired ? (
                <Dismiss20Regular />
              ) : (
                <Send20Regular />
              )
            }
            disabled={
              isSaving ||
              hasInvalidSchedule ||
              cannotPublishExpiredSchedule ||
              (!isBannerPublishedAndNotExpired && !bannerForm.message.trim())
            }
            onClick={handleTogglePublish}
          >
            {isBannerPublishedAndNotExpired
              ? t("systemInfoBanner.unpublish")
              : t("systemInfoBanner.publish")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function createEmptyBannerForm(): UpsertSystemInfoBannerRequest {
  return {
    message: "",
    isPublished: false,
    validFromUtc: null,
    validToUtc: null,
  };
}

function createBannerForm(
  systemInfoBanner?: SystemInfoBanner | null
): UpsertSystemInfoBannerRequest {
  if (!systemInfoBanner) {
    return createEmptyBannerForm();
  }

  return {
    message: systemInfoBanner.message,
    isPublished: systemInfoBanner.isPublished,
    validFromUtc: systemInfoBanner.validFromUtc ?? null,
    validToUtc: systemInfoBanner.validToUtc ?? null,
  };
}

function createBannerRequest(
  bannerForm: UpsertSystemInfoBannerRequest,
  scheduleForm: ScheduleForm,
  isPublished: boolean
): UpsertSystemInfoBannerRequest {
  return {
    message: bannerForm.message,
    isPublished,
    validFromUtc: toUtcIsoString(
      scheduleForm.validFromDate,
      scheduleForm.validFromTime
    ),
    validToUtc: toUtcIsoString(
      scheduleForm.validToDate,
      scheduleForm.validToTime
    ),
  };
}

function getSystemInfoBannerStateKey(
  systemInfoBanner?: SystemInfoBanner | null
): string {
  const bannerForm = createBannerForm(systemInfoBanner);

  return JSON.stringify(bannerForm);
}

function hasEditableChanges(
  savedBannerForm: UpsertSystemInfoBannerRequest,
  currentBannerForm: UpsertSystemInfoBannerRequest
): boolean {
  return (
    savedBannerForm.message !== currentBannerForm.message ||
    normalizeIsoString(savedBannerForm.validFromUtc) !==
      normalizeIsoString(currentBannerForm.validFromUtc) ||
    normalizeIsoString(savedBannerForm.validToUtc) !==
      normalizeIsoString(currentBannerForm.validToUtc)
  );
}

function createScheduleForm(
  validFromUtc?: string | null,
  validToUtc?: string | null
): ScheduleForm {
  return {
    validFromDate: getLocalDateInputValue(validFromUtc),
    validFromTime: getLocalTimeInputValue(validFromUtc),
    validToDate: getLocalDateInputValue(validToUtc),
    validToTime: getLocalTimeInputValue(validToUtc),
  };
}

function getLocalDateInputValue(value?: string | null): string {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const localTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 10);
}

function getLocalTimeInputValue(value?: string | null): string {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const localTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(11, 16);
}

function getDatePickerValue(value: string): Date | undefined {
  if (!value) {
    return undefined;
  }

  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return undefined;
  }

  return new Date(year, month - 1, day);
}

function toDateInputValue(value?: Date | null): string {
  if (!value) {
    return "";
  }

  const year = value.getFullYear();
  const month = (value.getMonth() + 1).toString().padStart(2, "0");
  const day = value.getDate().toString().padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function formatSwedishDate(value?: Date): string {
  if (!value) {
    return "";
  }

  return value.toLocaleDateString("sv-SE");
}

function getTimeOrDefault(value: string): string {
  return normalizeCompleteTimeInput(value) ?? "00:00";
}

function formatTimeInputValue(value: string): string {
  const numericValue = value.replace(/[^\d:]/g, "");

  if (numericValue.includes(":")) {
    const [hourValue, minuteValue = ""] = numericValue.split(":");

    return `${hourValue.slice(0, 2)}:${minuteValue.slice(0, 2)}`;
  }

  const digits = numericValue.slice(0, 4);

  if (digits.length <= 2) {
    return digits;
  }

  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

function isScheduleTimeInvalid(dateValue: string, timeValue: string): boolean {
  return Boolean(dateValue) && !isCompleteTimeInput(timeValue);
}

function isScheduleRangeInvalid(scheduleForm: ScheduleForm): boolean {
  const validFrom = getScheduleDateTime(
    scheduleForm.validFromDate,
    scheduleForm.validFromTime
  );
  const validTo = getScheduleDateTime(
    scheduleForm.validToDate,
    scheduleForm.validToTime
  );

  return Boolean(validFrom && validTo && validTo < validFrom);
}

function isScheduleExpired(
  scheduleForm: ScheduleForm,
  currentTime: Date
): boolean {
  const validTo = getScheduleDateTime(
    scheduleForm.validToDate,
    scheduleForm.validToTime
  );

  return Boolean(validTo && validTo < currentTime);
}

function isPublishedAndNotExpired(
  bannerForm: UpsertSystemInfoBannerRequest,
  scheduleForm: ScheduleForm,
  currentTime: Date
): boolean {
  if (!bannerForm.isPublished || !bannerForm.message.trim()) {
    return false;
  }

  const validTo = getScheduleDateTime(
    scheduleForm.validToDate,
    scheduleForm.validToTime
  );

  return !validTo || validTo >= currentTime;
}

function getScheduleDateTime(
  dateValue: string,
  timeValue: string
): Date | null {
  if (!dateValue) {
    return null;
  }

  const normalizedTime = normalizeCompleteTimeInput(timeValue);
  if (!normalizedTime) {
    return null;
  }

  const date = new Date(`${dateValue}T${normalizedTime}`);

  return Number.isNaN(date.getTime()) ? null : date;
}

function isCompleteTimeInput(value: string): boolean {
  return normalizeCompleteTimeInput(value) !== null;
}

function normalizeCompleteTimeInput(value: string): string | null {
  const timeMatch = /^(\d{1,2}):([0-5]\d)$/.exec(value);
  if (!timeMatch) {
    return null;
  }

  const hour = Number(timeMatch[1]);
  if (hour > 23) {
    return null;
  }

  return `${hour.toString().padStart(2, "0")}:${timeMatch[2]}`;
}

function toUtcIsoString(dateValue: string, timeValue: string): string | null {
  if (!dateValue) {
    return null;
  }

  const date = new Date(`${dateValue}T${getTimeOrDefault(timeValue)}`);

  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function normalizeIsoString(value?: string | null): string | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
}

export default SystemInfoBannerAdminPage;
